import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FaBars, FaCrown, FaGamepad, FaKeyboard, FaTimes } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { Button } from "../../ui";
import Logo from "./Logo";
import CodeEntryModal from "./CodeEntryModal";

const NAV = [
  { label: "كيف تلعب", to: { pathname: "/", hash: "#how" } },
  { label: "الفئات", to: { pathname: "/", hash: "#modes" } },
  { label: "الاشتراك", to: "/pricing" },
  { label: "التطبيق", to: { pathname: "/", hash: "#download" } },
];

const navClass = ({ isActive }) =>
  `px-3 py-2 rounded-lg text-[15px] font-semibold transition-colors ${
    isActive ? "text-white" : "text-white/70 hover:text-white hover:bg-white/5"
  }`;

// Site-wide header for the public pages: navigation, the "do you have a
// code?" entry point (game or discount), and the account buttons.
const SiteHeader = () => {
  const { isAuthenticated } = useAuth();
  const { isPremium } = useSubscription();
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);

  // Close the mobile menu on navigation.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, hash]);

  const accountButtons = isAuthenticated ? (
    <>
      {isPremium ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/30 px-3 h-9 text-sm font-bold text-primary">
          <FaCrown aria-hidden="true" /> مميز
        </span>
      ) : (
        <Button to="/pricing" variant="ghost" size="sm" icon={<FaCrown className="text-primary" />}>
          اشترك
        </Button>
      )}
      <Button to="/app/games" size="sm" icon={<FaGamepad />}>
        ألعابي
      </Button>
    </>
  ) : (
    <>
      <Button to="/login" variant="ghost" size="sm">
        تسجيل الدخول
      </Button>
      <Button to="/register" size="sm">
        ابدأ مجاناً
      </Button>
    </>
  );

  return (
    <header
      dir="rtl"
      className="sticky top-0 z-40 border-b border-white/10 bg-header/85 backdrop-blur-md font-sans"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center gap-3">
        <Logo to="/" className="w-11 h-11" />

        <nav className="hidden lg:flex items-center gap-1 mr-4" aria-label="الأقسام">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={typeof item.to === "string" ? navClass : () => navClass({ isActive: false })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setCodeOpen(true)}
          className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-white/15 text-sm font-bold text-white/85 hover:text-white hover:bg-white/10"
        >
          <FaKeyboard className="text-primary" aria-hidden="true" />
          عندك كود؟
        </button>

        <div className="hidden md:flex items-center gap-2">{accountButtons}</div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden -ml-1 p-2.5 rounded-lg text-white hover:bg-white/10"
          aria-expanded={menuOpen}
          aria-controls="site-menu"
          aria-label={menuOpen ? "إغلاق القائمة" : "القائمة"}
        >
          {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div id="site-menu" className="md:hidden border-t border-white/10 bg-header">
          <nav className="mx-auto max-w-6xl px-4 py-3 flex flex-col" aria-label="القائمة">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="px-2 py-3 text-base font-semibold text-white/85 hover:text-white border-b border-white/5"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setCodeOpen(true);
              }}
              className="px-2 py-3 text-base font-semibold text-right text-white/85 hover:text-white flex items-center gap-2"
            >
              <FaKeyboard className="text-primary" aria-hidden="true" /> عندك كود؟
            </button>
            <div className="flex flex-wrap gap-2 pt-3">{accountButtons}</div>
          </nav>
        </div>
      )}

      <CodeEntryModal open={codeOpen} onClose={() => setCodeOpen(false)} />
    </header>
  );
};

export default SiteHeader;
